import { ApolloError, gql, useMutation } from '@apollo/client';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useDebouncedEffect } from 'src/hooks/useDebouncedEffect';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import {
  AUTOSAVE_TIMEOUT_MS,
  ChecksTimeRange,
  checksTimeRangeInSeconds,
} from '../../checksConfigPage/constants';
import { SharedCheckConfigCardProps } from '../common/SharedCheckConfigCardProps';
import { UseGraphSettingsButton } from '../common/UseGraphSettingsButton';
import { VariantCheckConfigFragment } from '../VariantCheckConfigFragment';

const variantCheckConfigTimeRangeMutation = gql`
  mutation VariantCheckConfigTimeRangeMutation(
    $graphId: ID!
    $graphVariant: String!
    $useGraphSettings: Boolean!
    $timeRangeSeconds: Long
    $operationCountThreshold: Int
  ) {
    service(id: $graphId) {
      variant(name: $graphVariant) {
        updateCheckConfigurationTimeRange(
          useGraphSettings: $useGraphSettings
          timeRangeSeconds: $timeRangeSeconds
          operationCountThreshold: $operationCountThreshold
        ) {
          id
          ...VariantCheckConfigFragment
        }
      }
    }
  }
  ${VariantCheckConfigFragment}
`;

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  useGraphSettings: Yup.boolean().required(),
  timeRangeSeconds: Yup.mixed()
    .oneOf(Object.values(checksTimeRangeInSeconds))
    .nullable(),
  operationCountThreshold: Yup.number().min(1, 'min: 1').nullable(),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

type Props = SharedCheckConfigCardProps;

export const TimeRangeCard = ({
  graphRef,
  variantCheckConfig,
  graphCheckConfig,
  roles,
}: Props) => {
  const [updateConfigError, setUpdateConfigError] =
    useState<ApolloError | null>(null);

  const [updateCheckConfig] = useMutation<
    GraphQLTypes.VariantCheckConfigTimeRangeMutation,
    GraphQLTypes.VariantCheckConfigTimeRangeMutationVariables
  >(variantCheckConfigTimeRangeMutation, {
    onError: setUpdateConfigError,
  });

  const useGraphSettings = variantCheckConfig.timeRangeConfig.useGraphSettings;

  const {
    values,
    errors,
    dirty,
    isSubmitting,
    handleChange,
    setFieldValue,
    submitForm,
  } = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      useGraphSettings,
      timeRangeSeconds: useGraphSettings
        ? graphCheckConfig.timeRangeSeconds
        : variantCheckConfig.timeRangeConfig.timeRangeSeconds,
      operationCountThreshold: useGraphSettings
        ? graphCheckConfig.operationCountThreshold
        : variantCheckConfig.timeRangeConfig.operationCountThreshold,
    },
    onSubmit: async (formValues) => {
      await updateCheckConfig({
        variables: {
          graphId: graphRef.graphId,
          graphVariant: graphRef.graphVariant,
          useGraphSettings: formValues.useGraphSettings,
          timeRangeSeconds: formValues.timeRangeSeconds,
          operationCountThreshold: formValues.operationCountThreshold,
        },
      });
    },
  });

  const clearErrorsAndHandleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setUpdateConfigError(null);
      handleChange(e);
    },
    [setUpdateConfigError, handleChange],
  );

  const submitIfDirty = useCallback(() => {
    if (dirty && Object.keys(errors).length === 0) {
      submitForm();
    }
  }, [dirty, errors, submitForm]);

  useDebouncedEffect(submitIfDirty, AUTOSAVE_TIMEOUT_MS, [values]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Time range</CardTitle>
        <CardDescription>
          Checks for <em>{graphRef.graphVariant}</em> will be run against
          operations from the specified time range:
        </CardDescription>

        <FormControl>
          <UseGraphSettingsButton
            label="Use Graph Settings"
            tooltipContent={
              values.useGraphSettings
                ? 'Default to the time range configured by this graph.'
                : undefined
            }
            isSelected={values.useGraphSettings}
            disabled={!roles.canWriteCheckConfiguration}
            onChange={(value) => {
              setFieldValue('useGraphSettings', value);
              if (value === true) {
                setFieldValue('timeRangeSeconds', undefined);
                setFieldValue('operationCountThreshold', undefined);
              }
              submitForm();
            }}
          />
        </FormControl>
      </CardHeader>
      <CardBody>
        <div className="flex flex-col flex-wrap gap-2 md:flex-row">
          <FormControl className="flex-1">
            <FormLabel className="sr-only">Time range selector</FormLabel>
            <Select
              autoFocus={!values.useGraphSettings}
              id="timeRangeSeconds"
              name="timeRangeSeconds"
              value={
                // Mirror what the graph-level default would be
                // without setting the form value.
                values.timeRangeSeconds ?? graphCheckConfig.timeRangeSeconds
              }
              isDisabled={
                !roles.canWriteCheckConfiguration ||
                values.useGraphSettings ||
                isSubmitting
              }
              onChange={clearErrorsAndHandleChange}
            >
              {Object.values(ChecksTimeRange).map((timeRangeValue) => (
                <option
                  key={`${checksTimeRangeInSeconds[timeRangeValue]}`}
                  value={`${checksTimeRangeInSeconds[timeRangeValue]}`}
                >
                  Within the {timeRangeValue}
                </option>
              ))}
            </Select>
          </FormControl>

          {/* Operation count */}
          <div className="flex flex-2 flex-col items-center gap-2 md:flex-row">
            <div className="flex justify-center text-center">
              <FormControl
                isInvalid={!!errors.operationCountThreshold}
                className="flex-col md:flex-row"
              >
                <FormLabel htmlFor="operationCountThreshold">
                  Include operations that have run at least
                </FormLabel>
                <Input
                  className="w-24 text-center"
                  type="number"
                  name="operationCountThreshold"
                  isDisabled={
                    !roles.canWriteCheckConfiguration ||
                    values.useGraphSettings ||
                    isSubmitting
                  }
                  onChange={clearErrorsAndHandleChange}
                  value={
                    // Mirror what the graph-level default would be
                    // without setting the form value.
                    values.operationCountThreshold ??
                    graphCheckConfig.operationCountThreshold
                  }
                  id="operationCountThreshold"
                />
                <FormErrorMessage className="justify-center">
                  {errors.operationCountThreshold}
                </FormErrorMessage>
              </FormControl>
            </div>
            <span>
              {format.pluralize(values.operationCountThreshold ?? 0, 'time')}.
            </span>
          </div>
        </div>

        {/* Form update error message */}
        {updateConfigError && <ErrorMessage error={updateConfigError} />}
      </CardBody>
    </Card>
  );
};
